import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader2, MoreHorizontal } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";

interface EmailCampaign {
  _id: string;
  subject: string;
  sendAt?: string;
  to?: string[];
  status?: "draft" | "scheduled" | "sent";
}

export function EmailCampaigns() {
  const [emailCampaigns, setEmailCampaigns] = useState<EmailCampaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const response = await fetch("/api/email-campaigns");

        if (response.ok) {
          const data = await response.json();
          const campaigns = data.emailCampaigns || [];
          // Sort by _id descending (newest first)
          campaigns.sort((a: EmailCampaign, b: EmailCampaign) => {
            return b._id.localeCompare(a._id);
          });
          setEmailCampaigns(campaigns);
        } else {
          console.error("Failed to fetch email campaigns");
        }

        setBreadcrumbs([
          { label: "Messages", path: "/messages" },
          { label: "Emails", path: "/messages/emails" },
        ]);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();

    return () => {
      clearBreadcrumbs();
    };
  }, []);

  const handleCompose = async () => {
    try {
      const response = await fetch("/api/email-campaigns", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create email campaign");
      }

      const data = await response.json();
      navigate(`/emails/${data.campaign._id}/edit`);
    } catch (error) {
      console.error("Error creating email campaign:", error);
      alert(error instanceof Error ? error.message : "Failed to create email campaign");
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDelete = async (campaignId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!confirm("Are you sure you want to delete this email campaign?")) {
      return;
    }

    setDeletingId(campaignId);
    try {
      const response = await fetch(`/api/email-campaigns/${campaignId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setEmailCampaigns(emailCampaigns.filter((campaign) => campaign._id !== campaignId));
      } else {
        const error = await response.json();
        alert(error.error || "Failed to delete email campaign");
      }
    } catch (error) {
      console.error("Error deleting email campaign:", error);
      alert("An error occurred while deleting the email campaign");
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-end mb-4">
        <Button onClick={handleCompose}>
          Compose
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Subject</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Contacts</TableHead>
            <TableHead className="w-[100px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {emailCampaigns && emailCampaigns.length > 0 ? (
            emailCampaigns.map((campaign) => (
              <TableRow 
                key={campaign._id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => {
                  const route = campaign.status === "draft"
                    ? `/emails/${campaign._id}/edit`
                    : `/messages/emails/${campaign._id}/report`;
                  navigate(route);
                }}
              >
                <TableCell>
                  <Badge 
                    className={
                      campaign.status === "draft"
                        ? "bg-gray-500 text-white"
                        : campaign.status === "scheduled"
                          ? "bg-blue-500 text-white whitespace-nowrap"
                          : "bg-green-500 text-white"
                    }
                  >
                    {campaign.status === "draft"
                      ? "Draft"
                      : campaign.status === "scheduled"
                        ? campaign.sendAt
                          ? `Scheduled ${formatDate(campaign.sendAt)}`
                          : "Scheduled"
                        : "Sent"}
                  </Badge>
                </TableCell>
                <TableCell>{campaign.subject}</TableCell>
                <TableCell>
                  {campaign.sendAt ? formatDate(campaign.sendAt) : "-"}
                </TableCell>
                <TableCell>
                  {campaign.to ? campaign.to.length : 0}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={deletingId === campaign._id}
                      >
                        {deletingId === campaign._id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <MoreHorizontal className="h-4 w-4" />
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={(e) => handleDelete(campaign._id, e)}
                        disabled={
                          (campaign.status !== "draft" && campaign.status !== "scheduled") ||
                          deletingId === campaign._id
                        }
                        className="text-destructive focus:text-destructive"
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} className="h-24 text-center">
                No email campaigns found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

